import React from 'react';
import Box, { BoxProps } from '@mui/material/Box';
export interface LogoProps {
  logoURL: string;
  tenantName: string;
  maxHeight?: string;
  maxWidth?: string;
  padding?: number;
  wrapperSx?: BoxProps['sx'];
}
/**
 * A React component which renders the tenant logo
 *
 * @param logoURL - height pixels number
 * @param tenantName - the tenant name to include on the image tag's 'alt' attribute
 * @param padding - (optional) any padding around the logo, defaults to 4
 * @param maxHeight - (optional) the max height of the logo, defaults to 150px
 * @param maxWidth - (optional) the max width of the logo, defaults to 150px
 */
const LogoComponent: React.FC<React.PropsWithChildren<LogoProps>> = ({
  logoURL,
  tenantName,
  padding = 4,
  maxHeight = '32px', // based on the last discussion with designers
  maxWidth = 'auto',
  wrapperSx,
}: LogoProps) => {
  // Extract pixel value from maxHeight for HTML attributes (fallback when CSS is disabled)
  // Only use pixel values, not percentages
  const heightInPixels = maxHeight?.endsWith('px') ? parseInt(maxHeight, 10) : 32;
  const widthInPixels = maxWidth?.endsWith('px') ? parseInt(maxWidth, 10) : undefined;
  return (
    <Box
      sx={{
        textAlign: 'center',
        p: padding,
        ...wrapperSx,
      }}
    >
      <img
        width={widthInPixels}
        height={heightInPixels}
        style={{
          verticalAlign: 'middle',
          display: 'inline-block',
          maxWidth,
          maxHeight,
          width: 'auto',
          height: 'auto',
        }}
        src={logoURL}
        alt={`${tenantName} logo`}
        data-testid="logo"
      />
    </Box>
  );
};
export const Logo = React.memo(LogoComponent);
